import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Users } from "lucide-react";
import Seo from "@/components/Seo";
import { Btn, Eyebrow, SplitHeadline, Reveal, ParallaxImage } from "@/components/Elements";
import { Logo, GoogleG, Stars } from "@/components/Badges";
import {
  HERO_STATS,
  RECRUIT_STATS,
  TRUST_ITEMS,
  WHY_CARDS,
  BENEFITS,
  BIO_1,
  PROGRAMS_PREVIEW,
  PROCESS_PREVIEW,
  REVIEWS,
  PHONE_DISPLAY,
  TEL_HREF,
  CALENDLY,
  MAILTO,
  AXEN_URL,
  GOOGLE_REVIEWS_URL,
} from "@/data/content";

const HERO_SUB =
  "Joe Spellman — Branch Manager & National Recruiter — helps Florida families navigate the mortgage process with honest guidance, competitive rates, and a personal touch that big banks simply cannot match.";

const TRUST_BORDERS = [
  "max-sm:border-b sm:border-b lg:border-b-0",
  "max-sm:border-b sm:border-b sm:border-l lg:border-b-0",
  "max-sm:border-b lg:border-l",
  "sm:border-l",
];

export default function Home() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    document.title =
      "Family First Mortgage USA | Joe Spellman | Florida Mortgage Broker | NMLS #2142845";
  }, [hash]);

  return (
    <div>
      <Seo
        title="Family First Mortgage USA | Joe Spellman | Florida Mortgage Broker | NMLS #2142845"
        description="Florida mortgage broker Joe Spellman — 320+ lenders shopped, 9–16 day average closings, honest personal guidance. Call (863) 559-8288. Spring Hill, FL."
      />

      {/* 1. HERO */}
      <section className="relative flex min-h-screen flex-col bg-navy" data-testid="home-hero">
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          {/* Mobile / reduced-data: static image fallback (frame from the hero clip) */}
          <img
            src="/images/hero-poster.jpg"
            alt=""
            className="kenburns absolute inset-0 h-full w-full object-cover md:hidden"
          />
          {/* Desktop / tablet: slow looping muted background video, image poster fallback */}
          <video
            className="absolute inset-0 hidden h-full w-full object-cover md:block"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/images/hero-poster.jpg"
          >
            <source src="/videos/hero.mp4" type="video/mp4" />
          </video>
        </div>
        {/* Bolder dark overlays so the headline stays legible against a bright sky */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/70 to-navy/40"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-navy via-navy/35 to-navy/45"
          aria-hidden="true"
        />

        <div className="wrap relative flex flex-1 items-center pt-32 pb-16 lg:pt-36">
          <div className="grid w-full items-center gap-14 lg:grid-cols-[1.25fr_1fr]">
            <Reveal>
              <Logo transparent className="h-16 w-auto md:h-20" />
              <h1 className="mt-8 font-serif text-5xl font-bold leading-[1.04] tracking-tight text-white drop-shadow-[0_2px_24px_rgba(8,18,38,0.65)] sm:text-6xl lg:text-7xl">
                Your Dream Home
                <span className="block font-normal italic text-gold">Starts Here.</span>
              </h1>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-white/90 drop-shadow-[0_1px_12px_rgba(8,18,38,0.55)] sm:text-lg">
                {HERO_SUB}
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Btn variant="gold" href={CALENDLY} external testId="hero-get-preapproved">
                  Get Pre-Approved — It&rsquo;s Free
                </Btn>
                <Btn variant="outlineWhite" href={TEL_HREF} arrow={false} testId="hero-call-button">
                  <span className="inline-flex items-center gap-2">{PHONE_DISPLAY}</span>
                </Btn>
              </div>
            </Reveal>

            <Reveal
              as="aside"
              delay={120}
              className="w-full max-w-md justify-self-auto overflow-hidden rounded-3xl border-l-4 border-gold bg-navylight/80 p-8 backdrop-blur-md md:p-10 lg:justify-self-end"
              data-testid="hero-recruiting-card"
            >
              <div className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.22em] text-gold">
                <span className="h-px w-8 bg-gold" aria-hidden="true" />
                <span>National Recruiting</span>
              </div>
              <h2 className="mt-4 font-serif text-3xl font-bold leading-tight text-white">
                Build Your Career
                <span className="block font-normal italic text-gold">at the Best</span>
              </h2>
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-7">
                {RECRUIT_STATS.map((s) => (
                  <div key={s.label}>
                    <p className="font-serif text-3xl font-bold text-gold">{s.value}</p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-mist">{s.label}</p>
                  </div>
                ))}
              </div>
              <Btn variant="gold" href="#join-team" className="mt-9" testId="hero-explore-recruiting">
                Explore National Recruiting
              </Btn>
            </Reveal>
          </div>
        </div>

        <div className="relative border-t border-white/15" data-testid="hero-stat-row">
          <div className="wrap grid grid-cols-2 lg:grid-cols-4">
            {HERO_STATS.map((s, i) => (
              <div
                key={s.label}
                className={`px-5 py-7 lg:px-8 lg:py-8 border-white/10 ${
                  i % 2 === 0 ? "border-r" : ""
                } ${i < 2 ? "border-b lg:border-b-0" : ""} lg:border-l lg:first:border-l-0`}
              >
                <p className="font-serif text-2xl font-bold text-gold md:text-3xl">{s.value}</p>
                <p className="mt-1.5 text-[11px] uppercase tracking-[0.12em] text-mist/90">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. TRUST BAR */}
      <section className="border-t border-white/10 bg-navy" data-testid="trust-bar">
        <div className="wrap grid sm:grid-cols-2 lg:grid-cols-4">
          {TRUST_ITEMS.map((t, i) => (
            <div key={t.lead} className={`border-white/10 px-5 py-7 lg:px-8 ${TRUST_BORDERS[i]}`}>
              {t.google ? (
                <div className="flex items-center gap-3">
                  <GoogleG className="h-6 w-6" />
                  <Stars className="text-sm" />
                </div>
              ) : (
                <p className="text-sm font-medium text-white">{t.lead}</p>
              )}
              {t.google ? (
                <p className="mt-2 text-sm text-mist">
                  <span className="font-medium text-white">{t.lead}</span> · {t.rest}
                </p>
              ) : (
                <p className="mt-2 text-sm text-mist">{t.rest}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* 3. WHY FAMILIES CHOOSE JOE */}
      <section className="bg-cream py-24 md:py-28" data-testid="why-section">
        <div className="wrap">
          <Reveal>
            <Eyebrow>Why Families Choose Joe</Eyebrow>
            <SplitHeadline line1="Banking for You," line2="Not for the Bank." className="mt-5" />
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {WHY_CARDS.map((c, i) => (
              <Reveal
                key={c.title}
                delay={i * 90}
                className="rounded-2xl border border-line bg-white p-8 transition-shadow duration-300 hover:shadow-[0_24px_60px_rgba(18,38,74,0.10)]"
                data-testid={`why-card-${i + 1}`}
              >
                <span className="font-serif text-6xl font-normal italic text-goldpale">0{i + 1}</span>
                <h3 className="mt-6 font-serif text-xl font-bold text-navy">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink">{c.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. BENEFITS */}
      <section className="bg-navy py-24 md:py-28" data-testid="benefits-section">
        <div className="wrap">
          <Reveal>
            <Eyebrow>The Joe Spellman Difference</Eyebrow>
            <SplitHeadline light line1="Benefits of Working" line2="with Joe" className="mt-5" />
            <p className="mt-6 max-w-xl text-base text-mist sm:text-lg">
              Not all mortgage professionals are the same. Here&rsquo;s what sets Joe apart from the competition.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal
                key={b.title}
                delay={(i % 3) * 90}
                className="rounded-2xl border border-white/10 bg-navylight p-8 transition-colors duration-300 hover:border-gold/40"
                data-testid="benefit-card"
              >
                <b.icon size={28} strokeWidth={1.25} className="text-gold" aria-hidden="true" />
                <h3 className="mt-6 font-serif text-lg font-bold text-white">{b.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">{b.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. MEET YOUR ADVISOR */}
      <section className="bg-white py-24 md:py-28" data-testid="advisor-section">
        <div className="wrap grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal className="relative mx-auto w-full max-w-md lg:mx-0">
            <div className="overflow-hidden rounded-3xl bg-cream p-6 md:p-8">
              <img
                src="/images/joe-spellman-headshot.jpg"
                alt="Joe Spellman, Branch Manager, Family First Mortgage USA"
                className="aspect-[4/5] w-full rounded-2xl object-cover"
                style={{ objectPosition: "48% top" }}
                data-testid="advisor-portrait"
              />
            </div>
            <div className="absolute -bottom-5 left-6 flex items-center gap-3 rounded-2xl border border-line bg-white/90 px-5 py-4 shadow-[0_18px_45px_rgba(18,38,74,0.14)] backdrop-blur-md lg:-left-6">
              <GoogleG className="h-6 w-6" />
              <span>
                <span className="flex items-center gap-2">
                  <Stars className="text-sm" />
                  <span className="text-sm font-semibold text-navy">5.0</span>
                </span>
                <span className="mt-0.5 block text-xs text-ink/70">55+ Google Reviews</span>
              </span>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <Eyebrow>Meet Your Advisor</Eyebrow>
            <h2 className="mt-5 font-serif text-4xl font-bold tracking-tight text-navy sm:text-5xl">Joe Spellman</h2>
            <p className="mt-3 text-[12px] font-medium uppercase tracking-[0.18em] text-gold">
              Branch Manager &amp; National Recruiter — NEXA Lending
            </p>
            <p className="mt-6 text-base leading-relaxed text-ink sm:text-lg">{BIO_1}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Btn variant="navy" href="/about" testId="advisor-full-bio">
                Full Bio
              </Btn>
              <Btn variant="outlineNavy" href={CALENDLY} external arrow={false} testId="advisor-book-consultation">
                Book a Consultation
              </Btn>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. LOAN PROGRAMS PREVIEW */}
      <section className="bg-cream py-24 md:py-28" data-testid="programs-preview">
        <div className="wrap">
          <Reveal>
            <Eyebrow>Loan Programs</Eyebrow>
            <SplitHeadline line1="Find Your" line2="Perfect Loan" className="mt-5" />
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PROGRAMS_PREVIEW.map((p, i) => (
              <Reveal
                key={p.name}
                delay={i * 80}
                className="rounded-2xl border border-line bg-white p-7 transition-shadow duration-300 hover:shadow-[0_24px_60px_rgba(18,38,74,0.10)]"
                data-testid={`program-card-${p.name.toLowerCase()}`}
              >
                <span className="block h-px w-8 bg-gold" aria-hidden="true" />
                <h3 className="mt-5 font-serif text-xl font-bold text-navy">{p.name}</h3>
                <p className="mt-2 font-serif italic text-gold">{p.tagline}</p>
                <Link
                  to="/loan-programs"
                  className="group mt-5 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.1em] text-navy transition-colors duration-150 hover:text-gold"
                  data-testid={`program-learn-${p.name.toLowerCase()}`}
                >
                  Learn More
                  <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1">
                    →
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Btn variant="navy" href="/loan-programs" testId="view-all-programs">
              View All Programs
            </Btn>
          </div>
        </div>
      </section>

      {/* 7. PROCESS PREVIEW */}
      <section className="bg-navylight py-24 md:py-28" data-testid="process-preview">
        <div className="wrap">
          <div className="grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS_PREVIEW.map((p, i) => (
              <Reveal key={p.title} delay={i * 80} className="border-t border-white/15 pt-6">
                <span className="font-serif text-5xl font-normal italic text-goldpale">0{i + 1}</span>
                <h3 className="mt-4 font-serif text-lg font-bold text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-mist">{p.desc}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-16 text-center">
            <Btn variant="gold" href="/loan-process" testId="see-full-process">
              See Full Process
            </Btn>
          </div>
        </div>
      </section>

      {/* 8. CLIENT STORIES */}
      <section className="bg-white py-24 md:py-28" data-testid="reviews-section">
        <div className="wrap">
          <Reveal className="flex flex-wrap items-end justify-between gap-8">
            <div>
              <Eyebrow>Client Stories</Eyebrow>
              <SplitHeadline line1="Families" line2="We've Helped" className="mt-5" />
            </div>
            <div
              className="inline-flex items-center gap-3 rounded-full border border-line bg-white px-5 py-3"
              data-testid="reviews-badge"
            >
              <GoogleG className="h-5 w-5" />
              <Stars className="text-sm" />
              <span className="text-sm text-ink">5.0 · 55 Google Reviews</span>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <Reveal
                key={r.name}
                as="figure"
                delay={(i % 3) * 90}
                className="relative rounded-2xl border border-line bg-cream p-7 transition-shadow duration-300 hover:shadow-[0_24px_60px_rgba(18,38,74,0.10)]"
                data-testid="review-card"
              >
                <Stars className="text-sm" />
                <GoogleG className="absolute right-7 top-7 h-5 w-5" />
                <blockquote className="mt-4 font-serif text-[15px] italic leading-relaxed text-navy/90">
                  &ldquo;{r.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy font-serif text-sm text-gold"
                    aria-hidden="true"
                  >
                    {r.initials}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-navy">{r.name}</span>
                    <span className="block text-xs text-ink/60">Google Review</span>
                  </span>
                </figcaption>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Btn variant="outlineNavy" href={GOOGLE_REVIEWS_URL} external testId="view-all-google-reviews">
              View All 55+ Google Reviews
            </Btn>
          </div>
        </div>
      </section>

      {/* 8.5 LIFESTYLE PARALLAX BAND */}
      <section className="relative h-[48vh] min-h-[320px] overflow-hidden" data-testid="lifestyle-band" aria-hidden="true">
        <ParallaxImage src="/images/lifestyle-living.jpg" alt="" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/25 to-navy/40" />
      </section>

      {/* 9. RECRUITING BAND */}
      <section id="join-team" className="scroll-mt-6 bg-navy py-24 md:py-28" data-testid="join-team">
        <Reveal className="wrap text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-gold">
            <Users size={26} strokeWidth={1.25} className="text-gold" aria-hidden="true" />
          </span>
          <Eyebrow center className="mt-8">
            Are You a Loan Officer?
          </Eyebrow>
          <h2 className="mt-5 font-serif text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl">
            Join Joe&rsquo;s Team — <em className="font-normal italic text-gold">Nationally</em>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Btn variant="gold" href={`${MAILTO}?subject=NEXA%20Recruiting`} testId="join-explore-opportunity">
              Explore the Opportunity
            </Btn>
            <Btn variant="outlineWhite" href={AXEN_URL} external arrow={false} testId="join-earn-with-axen">
              Earn with Axen
            </Btn>
          </div>
        </Reveal>
      </section>

      {/* 10. FINAL CTA */}
      <section className="relative overflow-hidden py-28 md:py-36" data-testid="final-cta">
        <ParallaxImage src="/images/hero-golden.jpg" alt="" />
        <div className="absolute inset-0 bg-navy/85" aria-hidden="true" />
        <Reveal className="wrap relative text-center">
          <SplitHeadline light line1="Get Your Exact Rate" line2="Talk to Joe Today." className="mt-2" />
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Btn variant="gold" href={CALENDLY} external testId="final-get-preapproved">
              Get Pre-Approved Free
            </Btn>
            <Btn variant="outlineWhite" href={CALENDLY} external testId="final-schedule-consultation">
              Schedule a Consultation
            </Btn>
          </div>
          <p className="mt-8 text-sm text-mist">
            Or call directly:{" "}
            <a href={TEL_HREF} data-testid="final-phone" className="font-medium text-white underline decoration-gold underline-offset-4 transition-colors hover:text-gold">
              {PHONE_DISPLAY}
            </a>
          </p>
        </Reveal>
      </section>
    </div>
  );
}
